import { useState, useEffect } from 'react';

/**
 * Hook pour détecter la section active lors du scroll
 * @param {Array<string>} sectionIds - IDs des sections à surveiller
 * @param {number} offset - Offset en pixels depuis le haut
 * @returns {string} ID de la section active
 */
export function useScrollSpy(sectionIds = [], offset = 150) {
  const [activeSection, setActiveSection] = useState(sectionIds[0] || '');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offset;

      // Trouver la section actuellement visible
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const sectionId = sectionIds[i];
        const element = document.getElementById(sectionId);

        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    // Écouter le scroll
    window.addEventListener('scroll', handleScroll);

    // Appel initial
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sectionIds, offset]);

  return activeSection;
}

/**
 * Hook pour détecter si on a scrollé
 * @param {number} threshold - Seuil en pixels
 * @returns {boolean} true si scrollé au-delà du seuil
 */
export function useScrolled(threshold = 100) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return isScrolled;
}

/**
 * Fonction helper pour scroller vers une section
 * @param {string} sectionId - ID de la section cible
 * @param {number} offset - Offset depuis le haut
 */
export function scrollToSection(sectionId, offset = 80) {
  const element = document.getElementById(sectionId);
  if (element) {
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
